import React from 'react';
import { Tag, Package2, Percent, ExternalLink, Trash2, GripVertical, Edit2 } from 'lucide-react';
import { useDesignColors } from '../../hooks/useDesignColors';
import type { Product } from '../../hooks/useProductSystem';
import { useThemeSettings } from '../../hooks/useThemeSettings';

interface ProductCardProps {
  product: Product;
  onDelete: () => void;
  onDragStart: () => void;
  onShowDetails: () => void;
  onEdit: () => void;
  viewMode?: 'grid' | 'list';
}

export function ProductCard({ product, onDelete, onDragStart, onShowDetails, onEdit, viewMode = 'grid' }: ProductCardProps) {
  const { colors } = useDesignColors();
  const { theme } = useThemeSettings();
  const isDark = theme.mode === 'dark';

  const formatPrice = (price: number, discount?: number) => {
    if (!discount) return `${price} ${product.currency}`;
    
    const discountedPrice = price * (1 - discount / 100);
    return (
      <div className="flex flex-col">
        <span className="text-red-400 font-medium">
          {discountedPrice.toFixed(2)} {product.currency}
        </span>
        <span className="text-xs line-through text-gray-500">
          {price} {product.currency}
        </span>
      </div>
    );
  };

  // Rendu en mode liste (sans image ou image plus petite)
  if (viewMode === 'list') {
    return (
      <div 
        className={`flex items-center gap-4 p-4 rounded-xl border ${
          isDark ? 'bg-gray-900/30 border-white/10' : 'bg-white border-gray-200'
        } hover:shadow-md transition-shadow cursor-pointer w-full`}
        onClick={onShowDetails}
      >
        <div 
          className="flex items-center justify-center h-12 aspect-square rounded-lg overflow-hidden bg-black/20 shrink-0"
          draggable
          onDragStart={onDragStart}
        >
          {product.image_url ? (
            <img 
              src={product.image_url} 
              alt={product.name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <Package2 className="w-6 h-6 text-gray-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <h4 className={`text-base font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {product.name}
            </h4>
            
            <div className="flex items-center gap-1 shrink-0">
              {product.category && (
                <span 
                  className="inline-block px-2 py-1 text-xs rounded font-medium"
                  style={{
                    backgroundColor: `${colors.gradientStart}15`,
                    color: colors.gradientStart
                  }}
                >
                  {product.category}
                </span>
              )}
              
              <span className={`whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {product.currency || ''} {typeof product.price === 'number' ? product.price : 0}
              </span>
              
              {product.metadata && typeof product.metadata === 'object' && 
               'redirect_url' in product.metadata && product.metadata.redirect_url && (
                <a
                  href={String(product.metadata.redirect_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className={`p-2 rounded-full ${
              isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            } transition-colors`}
          >
            <Edit2 className="w-4 h-4 text-indigo-500" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className={`p-2 rounded-full ${
              isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            } transition-colors`}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
          <span
            className={`p-2 rounded-full cursor-grab ${
              isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            } transition-colors`}
            draggable
            onDragStart={onDragStart}
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </span>
        </div>
      </div>
    );
  }

  // Rendu par défaut (mode grille)
  return (
    <div 
      className={`p-4 rounded-xl border ${
        isDark ? 'bg-gray-900/30 border-white/10' : 'bg-white border-gray-200'
      } hover:shadow-md transition-shadow cursor-pointer w-full`}
      onClick={onShowDetails}
    >
      <div className="flex justify-end mb-2">
        <div className="flex gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className={`p-1.5 rounded-full ${
              isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            } transition-colors`}
          >
            <Edit2 className="w-4 h-4 text-indigo-500" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className={`p-1.5 rounded-full ${
              isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            } transition-colors`}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
          <span
            className={`p-1.5 rounded-full cursor-grab ${
              isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            } transition-colors`}
            draggable
            onDragStart={onDragStart}
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </span>
        </div>
      </div>
      
      <div 
        className="aspect-square rounded-lg overflow-hidden bg-black/20 mb-4"
        draggable
        onDragStart={onDragStart}
      >
        {product.image_url ? (
          <img 
            src={product.image_url} 
            alt={product.name} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package2 className="w-10 h-10 text-gray-400" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <h4 className={`text-lg font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {product.name}
          </h4>
          {product.metadata && typeof product.metadata === 'object' && 
           'redirect_url' in product.metadata && product.metadata.redirect_url && (
            <a
              href={String(product.metadata.redirect_url)}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
        
        {product.description && (
          <p className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {product.description}
          </p>
        )}
        
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {product.category && (
            <span 
              className="inline-block px-2 py-1 text-xs rounded font-medium"
              style={{
                backgroundColor: `${colors.gradientStart}15`,
                color: colors.gradientStart
              }}
            >
              {product.category}
            </span>
          )}
          
          {product.discount ? (
            <div className="ml-auto flex items-center gap-1">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} line-through`}>
                {product.currency || ''} {typeof product.price === 'number' ? product.price : 0}
              </span>
              <span className="text-sm font-medium text-red-500">
                {product.currency || ''} {typeof product.price === 'number' && typeof product.discount === 'number' 
                  ? (product.price * (1 - product.discount / 100)).toFixed(2) 
                  : 0}
              </span>
              <Percent className="w-3 h-3 text-red-500" />
            </div>
          ) : (
            <span className={`ml-auto text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {product.currency || ''} {typeof product.price === 'number' ? product.price : 0}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}